import React, { PureComponent } from "react";
import { Col, Form, Row } from "react-bootstrap";
import { Formik } from "formik";
import { omit, defaultTo } from "lodash";
import { ScrollToFieldError } from "utils/form";
import { daysOfMonthOptions, MONTHLY, END_OF_MONTH } from "utils/timeHelper";

import "react-datepicker/dist/react-datepicker.css";
import Button from "components/Button";
import DatePicker from "react-datepicker";
import moment from "moment";
import InfoTooltip from "components/InfoTooltip";
import type { Schedule } from "./scheduleTypes";

import "./SetupContribution.scss";

import * as yup from "yup";

interface ScheduleFormValues {
  frequency?: string;
  dayOne?: string | number;
  anchorPayDate?: string;
}

interface Props {
  setSchedule?: (schedule: Record<string, unknown>) => void;
  schedule: Schedule;
  showSaveButton?: boolean;
}

const schema = yup.object({
  frequency: yup.string().label("Payment Frequency").required(),
  dayOne: yup
    .string()
    .label("Payment Date")
    .when("frequency", {
      is: (frequency: string) => frequency !== MONTHLY,
      then: (s) => s.nullable(),
      otherwise: (s) => s.required(),
    }),
  anchorPayDate: yup
    .date()
    .label("First Payment Date")
    .when("frequency", {
      is: (frequency: string) => frequency === MONTHLY,
      then: (s) => s.nullable(),
      otherwise: (s) => s.required(),
    }),
});

export default class SchedulePicker extends PureComponent<Props> {
  _showDayOne = (frequency: string | undefined) => {
    return frequency === MONTHLY;
  };

  parseDayString = (day: string | number | undefined) => {
    return day === END_OF_MONTH ? 31 : parseInt(day as string);
  };

  _setSchedule = (recurringContributionValues: ScheduleFormValues) => {
    let newRecurringContribution: Record<string, unknown> = omit(
      {
        ...this.props.schedule,
        ...recurringContributionValues,
      },
      "dayOne"
    );
    if (recurringContributionValues.frequency === MONTHLY) {
      newRecurringContribution = {
        ...newRecurringContribution,
        dayOne: this.parseDayString(recurringContributionValues.dayOne),
      };
    }

    // some schedule props are not initialized with anchorPayDate - must initialize here if not set.
    return this.props.setSchedule!({
      ...newRecurringContribution,
      anchorPayDate: newRecurringContribution.anchorPayDate
        ? moment(
            newRecurringContribution.anchorPayDate as moment.MomentInput
          ).format("YYYY-MM-DD")
        : moment().format("YYYY-MM-DD"),
    });
  };

  render() {
    const startOfMonth = 1;
    return (
      <div className="schedule-picker-container">
        <Formik
          validateOnChange={false}
          validationSchema={schema}
          onSubmit={this._setSchedule}
          enableReinitialize={true}
          initialValues={
            {
              ...this.props.schedule,
              dayOne: defaultTo(this.props.schedule.dayOne, startOfMonth),
            } as ScheduleFormValues
          }
        >
          {({
            handleSubmit,
            handleChange,
            handleBlur,
            values,
            touched,
            errors,
            setFieldValue,
          }) => (
            <Form noValidate onSubmit={handleSubmit}>
              <ScrollToFieldError />
              <Form.Label style={{ fontWeight: 500, paddingBottom: 10 }}>
                How often would you like to contribute to your retirement
                account?
              </Form.Label>
              <Row className="mb-3" style={{ paddingBottom: 15 }}>
                <Form.Group as={Col} lg={6} controlId="formGroupFrequency">
                  <Form.Select
                    name="frequency"
                    value={values.frequency}
                    onChange={handleChange}
                    isInvalid={!!errors.frequency}
                    isValid={touched.frequency && !errors.frequency}
                  >
                    <option value="">Select Frequency</option>
                    <option value="WEEKLY">Every Week</option>
                    <option value="EVERY_OTHER_WEEK">Every Two Weeks</option>
                    <option value="MONTHLY">Every Month</option>
                  </Form.Select>
                  <Form.Control.Feedback type="invalid">
                    {errors.frequency}
                  </Form.Control.Feedback>
                </Form.Group>
              </Row>

              {this._showDayOne(values.frequency) && (
                <>
                  <Form.Label style={{ fontWeight: 500, paddingBottom: 10 }}>
                    Select your monthly contribution day{" "}
                    <InfoTooltip
                      tooltipBody="If a transfer falls on a banking holiday or weekend, the transfer will process the following business day."
                      placement="right"
                    />
                  </Form.Label>
                  <Row className="mb-3">
                    <Form.Group as={Col} lg={6} controlId="formGroupDayone">
                      <Form.Select
                        // @ts-expect-error - inert pass-through prop; Form.Select does not type `sm` (preserves original behavior)
                        sm={6}
                        name="dayOne"
                        value={values.dayOne}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        isInvalid={!!errors.dayOne}
                        isValid={touched.dayOne && !errors.dayOne}
                      >
                        <option value="" disabled>
                          Day of Month
                        </option>
                        {daysOfMonthOptions.map((item) => (
                          <option value={item.value} key={item.key}>
                            {item.value}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Row>
                </>
              )}
              {values.frequency && !this._showDayOne(values.frequency) && (
                <>
                  <Form.Label
                    style={{
                      fontWeight: 500,
                      paddingBottom: 10,
                    }}
                  >
                    Pick a start date for your recurring contribution{" "}
                    <InfoTooltip
                      tooltipBody="If a transfer falls on a banking holiday or weekend, the transfer will process the following business day."
                      placement="right"
                    />
                  </Form.Label>
                  <Row className="mb-3">
                    <Form.Group
                      as={Col}
                      lg={6}
                      controlId="formBasicDateOfBirth"
                    >
                      <div>
                        <DatePicker
                          className="form-control"
                          name="anchorPayDate"
                          autoComplete="off"
                          filterDate={(date) => {
                            const day = date.getDay();
                            const sunday = 0;
                            const saturday = 6;
                            return day !== sunday && day !== saturday;
                          }}
                          minDate={moment().add(1, "days").toDate()}
                          maxDate={moment().add(90, "days").toDate()}
                          value={values.anchorPayDate}
                          dateFormat="MM/dd/yyyy"
                          selected={
                            (values.anchorPayDate &&
                              new Date(values.anchorPayDate)) ||
                            null
                          }
                          onChange={(val: Date | null) => {
                            void setFieldValue("anchorPayDate", moment(val));
                          }}
                          // @ts-expect-error - inert pass-through props; DatePicker types neither isInvalid nor isValid (preserves original behavior)
                          isInvalid={!!errors.anchorPayDate}
                          isValid={
                            touched.anchorPayDate && !errors.anchorPayDate
                          }
                        />
                      </div>
                      {errors.anchorPayDate && touched.anchorPayDate && (
                        <div
                          className="invalid-feedback"
                          style={{ display: "block" }}
                        >
                          {errors.anchorPayDate}
                        </div>
                      )}
                    </Form.Group>
                  </Row>
                </>
              )}
              <div className="submit-row btn-row">
                <>
                  <div className="portfolio-lg-screen">
                    {/* @ts-expect-error - original JS omits the required `name` prop; preserving behavior (no name attribute rendered) */}
                    <Button />
                  </div>
                  <div className="portfolio-sm-screen">
                    {/* @ts-expect-error - original JS omits the required `name` prop; preserving behavior (no name attribute rendered) */}
                    <Button size="sm" />
                  </div>
                </>
              </div>
            </Form>
          )}
        </Formik>
      </div>
    );
  }
}
